<template>
    <div>
        <b-card title="TMS Uygulamasına Hoş Geldiniz! 🚀" class="mb-1">
            <b-card-text>TMS dünyasının müthiş ötesi imkanlarından faydalanmak için modüller arasında gezinebilirsiniz.</b-card-text>
        </b-card>

        <div class="row">
            <div class="col-12 mb-1" v-if="canShowShiftManagement">
                <shift-management-widget />
            </div>
            <div class="col-12 mb-1" v-if="canShowWaitingMyApprove">
                <transfer-waiting-my-approve />
            </div>
            <div class="col-12 mb-1" v-if="canShowFuelWaitingMyApprove">
                <fuel-transfer-waiting-my-approve />
            </div>
            <!-- Arabuluculuk ozeti: anlasma/odeme durumu (madde 18).
                 Arabuluculuk ekranini gorebilen kullaniciya gosterilir. -->
            <div class="col-lg-6 col-12 mb-1" v-if="canShowMediation">
                <mediation-summary />
            </div>

            <template v-if="canShowEmployeeReports">
                <div class="col-12 mb-1">
                    <b-card no-body class="dashboard-filter-card mb-0">
                        <div class="dashboard-filter-card__header">
                            <div class="d-flex align-items-center min-w-0">
                                <feather-icon icon="FilterIcon" size="16" class="mr-50 text-primary" />
                                <span class="font-weight-bolder">Uyarı Dashboardları</span>
                            </div>
                            <button class="btn btn-sm btn-flat-secondary" v-on:click="toggleFilterPanel">
                                <feather-icon :icon="showFilterPanel ? 'ChevronUpIcon' : 'ChevronDownIcon'" size="14" class="mr-25" />
                                {{ showFilterPanel ? "Filtreleri Gizle" : "Filtreleri Göster" }}
                            </button>
                        </div>
                        <b-collapse v-model="showFilterPanel">
                            <div class="dashboard-filter-card__body">
                                <div class="row">
                                    <div class="col-12 col-lg-7 mb-1 mb-lg-0">
                                        <label class="dashboard-filter-card__label">Görünür Dashboardlar</label>
                                        <div class="dashboard-filter-toggle-list">
                                            <label
                                                v-for="w in widgetCatalog"
                                                :key="w.key"
                                                class="dashboard-filter-toggle"
                                                :class="{ 'dashboard-filter-toggle--active': visibleWidgets.includes(w.key) }">
                                                <input type="checkbox" :value="w.key" v-model="visibleWidgets" />
                                                <span>{{ w.title }}</span>
                                            </label>
                                        </div>
                                        <div class="dashboard-filter-quick-actions">
                                            <button class="btn btn-sm btn-flat-primary" v-on:click="selectAllWidgets">Tümünü Seç</button>
                                            <button class="btn btn-sm btn-flat-secondary ml-50" v-on:click="clearAllWidgets">Tümünü Gizle</button>
                                        </div>
                                    </div>
                                    <div class="col-12 col-lg-5">
                                        <label class="dashboard-filter-card__label">Kritiklik Seviyesi</label>
                                        <div class="dashboard-filter-toggle-list">
                                            <label
                                                v-for="lvl in levelCatalog"
                                                :key="lvl.key"
                                                class="dashboard-filter-toggle dashboard-filter-toggle--level"
                                                :class="['dashboard-filter-toggle--' + lvl.variant, { 'dashboard-filter-toggle--active': enabledLevels.includes(lvl.key) }]">
                                                <input type="checkbox" :value="lvl.key" v-model="enabledLevels" />
                                                <span class="dashboard-stat__dot" :style="{ background: lvl.color }"></span>
                                                <span>{{ lvl.label }}</span>
                                            </label>
                                        </div>
                                        <div class="dashboard-filter-quick-actions">
                                            <button class="btn btn-sm btn-flat-primary" v-on:click="selectAllLevels">Tümünü Seç</button>
                                            <button class="btn btn-sm btn-flat-secondary ml-50" v-on:click="clearAllLevels">Tümünü Gizle</button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </b-collapse>
                    </b-card>
                </div>

                <template v-for="w in widgetCatalog">
                    <div
                        v-if="visibleWidgets.includes(w.key)"
                        :key="w.key"
                        class="col-12 col-md-6 col-xl-4 mb-1 dashboard-card-col">
                        <component :is="w.component" :enabled-levels="enabledLevels" />
                    </div>
                </template>

                <div v-if="visibleWidgets.length === 0" class="col-12 mb-1">
                    <b-card class="text-center mb-0">
                        <feather-icon icon="EyeOffIcon" size="32" class="text-muted mb-50" />
                        <p class="mb-0 text-muted">Görüntülenecek dashboard seçilmedi. Üstteki filtre panelinden dashboard seçin.</p>
                    </b-card>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { BCard, BCardText, BCollapse } from "bootstrap-vue";
import Notifications from "@/utilities/notifications";
import TransferWaitingMyApprove from "./components/TransferWaitingMyApprove.vue";
import FuelTransferWaitingMyApprove from "./components/FuelTransferWaitingMyApprove.vue";
import Auth from "@/utilities/auth";
import Psychotechnic from "./components/Psychotechnic.vue";
import HealthReport from "./components/HealthReport.vue";
import OHS from "./components/OHS.vue";
import DriverLicense from "./components/DriverLicense.vue";
import PhysicalDriverLicense from "./components/PhysicalDriverLicense.vue";
import SRC from "./components/SRC.vue";
import Over65Years from "./components/Over65Years.vue";
import HRConstants from "./components/HRConstants.vue";
import ShiftManagementWidget from "./components/ShiftManagementWidget.vue";
import MediationSummary from "./components/MediationSummary.vue";

const STORAGE_KEY = "tms.home.dashboardFilters.v1";

const WIDGET_CATALOG = [
    { key: "psychotechnic", title: "Psikoteknik", component: "psychotechnic" },
    { key: "ohs", title: "İSG Eğitimleri", component: "ohs" },
    { key: "healthReport", title: "Sağlık Raporu", component: "health-report" },
    { key: "over65", title: "65 Yaşı Geçen Sürücüler", component: "over-65-years" },
    { key: "driverLicense", title: "Dijital Ehliyet", component: "driver-license" },
    { key: "physicalDriverLicense", title: "Ehliyet", component: "physical-driver-license" },
    { key: "src", title: "SRC", component: "src" },
    { key: "hrConstants", title: "Süresi Dolan İK Parametreleri", component: "hr-constants" },
];

const LEVEL_CATALOG = [
    { key: "danger", label: "Kritik / Süresi Geçmiş", variant: "danger", color: "#ea5455" },
    { key: "warning", label: "Süresi Yaklaşan", variant: "warning", color: "#ff9f43" },
    { key: "secondary", label: "Beklemede / Diğer", variant: "secondary", color: "#6c757d" },
    { key: "none", label: "Kritiklik Yok", variant: "info", color: "#00cfe8" },
];

export default {
    components: {
        BCard,
        BCardText,
        BCollapse,
        TransferWaitingMyApprove,
        FuelTransferWaitingMyApprove,
        Psychotechnic,
        HealthReport,
        ohs: OHS,
        MediationSummary,
        src: SRC,
        DriverLicense,
        "physical-driver-license": PhysicalDriverLicense,
        Over65Years,
        ShiftManagementWidget,
        "hr-constants": HRConstants,
    },
    data() {
        return {
            widgetCatalog: WIDGET_CATALOG,
            levelCatalog: LEVEL_CATALOG,
            visibleWidgets: WIDGET_CATALOG.map((w) => w.key),
            enabledLevels: LEVEL_CATALOG.map((l) => l.key),
            showFilterPanel: true,
        };
    },
    created() {
        this.loadPreferences();
    },
    watch: {
        visibleWidgets: { handler() { this.savePreferences(); }, deep: true },
        enabledLevels: { handler() { this.savePreferences(); }, deep: true },
        showFilterPanel() { this.savePreferences(); },
    },
    mounted() {
        Notifications.getNotifications();
    },
    methods: {
        loadPreferences() {
            try {
                const raw = localStorage.getItem(STORAGE_KEY);
                if (!raw) return;
                const saved = JSON.parse(raw);
                if (Array.isArray(saved.visibleWidgets)) {
                    this.visibleWidgets = saved.visibleWidgets.filter((k) => WIDGET_CATALOG.some((w) => w.key === k));
                }
                if (Array.isArray(saved.enabledLevels)) {
                    this.enabledLevels = saved.enabledLevels.filter((k) => LEVEL_CATALOG.some((l) => l.key === k));
                }
                if (typeof saved.showFilterPanel === "boolean") this.showFilterPanel = saved.showFilterPanel;
            } catch (e) {
                /* ignore corrupt preferences */
            }
        },
        savePreferences() {
            try {
                localStorage.setItem(
                    STORAGE_KEY,
                    JSON.stringify({
                        visibleWidgets: this.visibleWidgets,
                        enabledLevels: this.enabledLevels,
                        showFilterPanel: this.showFilterPanel,
                    })
                );
            } catch (e) {
                /* storage unavailable */
            }
        },
        toggleFilterPanel() {
            this.showFilterPanel = !this.showFilterPanel;
        },
        selectAllWidgets() {
            this.visibleWidgets = WIDGET_CATALOG.map((w) => w.key);
        },
        clearAllWidgets() {
            this.visibleWidgets = [];
        },
        selectAllLevels() {
            this.enabledLevels = LEVEL_CATALOG.map((l) => l.key);
        },
        clearAllLevels() {
            this.enabledLevels = [];
        },
    },
    computed: {
        canShowShiftManagement() {
            return Auth.hasRole(["SHIFT_INFORMATION_PROCESS", "ADMIN"]);
        },
        canShowWaitingMyApprove() {
            return Auth.hasRole(["NETSIS_KASA_TRANSFER_APPROVE_AUTHORITY"]);
        },
        canShowFuelWaitingMyApprove() {
            return Auth.hasRole(["FUEL_TRANSFER_APPROVE_AUTHORITY"]);
        },
        canShowEmployeeReports() {
            return Auth.hasRole(["EMPLOYEE_DASHBOARD_REPORTS"]);
        },
        // Arabuluculuk ekranini gorebilen kullanici ozet kartini da gorur.
        canShowMediation() {
            return Auth.hasRole(["MEDIATION_PROCESS"]);
        },
    },
};
</script>

<style>
.dashboard-filter-card {
    border: 1px solid #ebe9f1;
    border-radius: 0.428rem;
}

.dashboard-filter-card__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0.6rem 0.85rem;
    border-bottom: 1px solid #ebe9f1;
    gap: 0.5rem;
}

.dashboard-filter-card__body {
    padding: 0.85rem;
}

.dashboard-filter-card__label {
    display: block;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: #6e6b7b;
    margin-bottom: 0.4rem;
    font-weight: 600;
}

.dashboard-filter-toggle-list {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
}

.dashboard-filter-toggle {
    display: inline-flex;
    align-items: center;
    gap: 0.35rem;
    padding: 0.3rem 0.65rem;
    border-radius: 999px;
    border: 1px solid #d8d6de;
    background: #fff;
    color: #5e5873;
    font-size: 0.78rem;
    font-weight: 500;
    cursor: pointer;
    margin: 0;
    user-select: none;
    transition: all 0.15s ease;
}

.dashboard-filter-toggle input {
    display: none;
}

.dashboard-filter-toggle:hover {
    border-color: #7367f0;
}

.dashboard-filter-toggle--active {
    background: rgba(115, 103, 240, 0.12);
    border-color: #7367f0;
    color: #7367f0;
}

.dashboard-filter-toggle--level.dashboard-filter-toggle--active.dashboard-filter-toggle--danger {
    background: rgba(234, 84, 85, 0.12);
    border-color: #ea5455;
    color: #ea5455;
}
.dashboard-filter-toggle--level.dashboard-filter-toggle--active.dashboard-filter-toggle--warning {
    background: rgba(255, 159, 67, 0.14);
    border-color: #ff9f43;
    color: #b86b15;
}
.dashboard-filter-toggle--level.dashboard-filter-toggle--active.dashboard-filter-toggle--secondary {
    background: rgba(108, 117, 125, 0.14);
    border-color: #6c757d;
    color: #6c757d;
}
.dashboard-filter-toggle--level.dashboard-filter-toggle--active.dashboard-filter-toggle--info {
    background: rgba(0, 207, 232, 0.14);
    border-color: #00cfe8;
    color: #0c8fa5;
}

.dashboard-filter-quick-actions {
    margin-top: 0.6rem;
}

.dashboard-card-col {
    display: flex;
}

.dashboard-card-col > * {
    width: 100%;
    height: 480px;
}

@media (max-width: 767.98px) {
    .dashboard-card-col > * { height: 420px; }
}

.dark-layout .dashboard-filter-card { border-color: #3b4253; }
.dark-layout .dashboard-filter-card__header { border-bottom-color: #3b4253; }
.dark-layout .dashboard-filter-toggle {
    background: #283046;
    border-color: #3b4253;
    color: #b4b7bd;
}
.dark-layout .dashboard-filter-toggle--active {
    background: rgba(115, 103, 240, 0.22);
    color: #d0d2d6;
}
.dark-layout .dashboard-filter-card__label { color: #b4b7bd; }
</style>
